import { __awaiter } from "tslib";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@vtblife/uikit-icons';
import { bubblePreventedHandlers, useSwipe } from '@vtblife/tools';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import s from './modal.module.css';
import keyCodes from '../../utils/keycodes';
import { noop } from '../../utils/function';
import useTransitionState from '../../hooks/use-transition-state';
import useKeyPress from '../../hooks/use-keypress';
import useDOMRoot from '../../hooks/use-dom-root';
import { useScrollLock } from '../../hooks/use-scroll-lock';
import { context, position, useMobile, origin, useFit, useScrollShift } from './utils';
import Header from './blocks/header';
import WideContent from './blocks/wide-content';
import Content from './blocks/content';
import Picture from './blocks/picture';
import Title from './blocks/title';
import Actions, { StickyActions } from './blocks/actions';
import { handleKeyPressEnter } from '../../utils/handle-keypress-enter';
import { ModalPreview } from './components/modal-content-layout';
const keys = [keyCodes.escape];
const closingTimeout = 400;
const swipeThreshold = 90;
export const Modal = ({ opened, onClose = noop, onBeforeClose = noop, size = 'm', closable = true, children, dataTest, mobile: mobileView = false, swipeToClose = true, preventionFilter = [], overflow = true, zIndex, }) => {
    const [key, setKey] = useState();
    const [top, setTop] = useState(origin);
    const modalRoot = useDOMRoot({ anchor: 'modal-launchpad', style: s.root });
    const containerRef = useRef(null);
    const scrollableRef = useRef(null);
    const contentRef = useRef(null);
    const mobile = useMobile(scrollableRef, opened);
    const fit = useFit({ scrollable: scrollableRef, content: contentRef, enabled: opened, mobile });
    const needRecalculation = useMemo(() => opened && !mobile && !fit, [opened, mobile, fit]);
    const [state, close] = useTransitionState(opened, { duration: closingTimeout });
    const ignoreScroll = fit ? {} : { 'data-scroll-lock-ignore': '' };
    const scrollShift = useScrollShift(state, scrollableRef.current);
    const prevention = useMemo(() => Object.entries(bubblePreventedHandlers)
        .filter(([key]) => !preventionFilter.includes(key))
        .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
    }, {}), [preventionFilter]);
    const preventor = useCallback(e => {
        e.preventDefault();
        e.stopPropagation();
    }, []);
    const handleClose = useCallback((event) => __awaiter(void 0, void 0, void 0, function* () {
        if (state !== 'show') {
            return;
        }
        preventor(event);
        onBeforeClose(event);
        yield close();
        onClose(event);
    }), [onClose, onBeforeClose, state, close, preventor]);
    const contextValue = useMemo(() => ({
        close: handleClose,
        closable,
        size,
        mobile: mobileView,
        dataTest,
    }), [handleClose, closable, size, mobileView, dataTest]);
    const calculateCloseTop = useCallback(() => {
        if (!scrollableRef.current) {
            return;
        }
        setTop(position(scrollableRef.current));
    }, []);
    const handleSwipeDown = useCallback(evt => {
        var _a;
        if (closable && ((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.scrollTop) === 0) {
            handleClose(evt);
        }
    }, [closable, handleClose]);
    useEffect(() => {
        if (opened) {
            setKey(Math.random());
        }
    }, [opened]);
    useEffect(() => {
        if (!modalRoot || !zIndex) {
            return;
        }
        if (state === 'appear') {
            modalRoot.style.zIndex = String(zIndex);
        }
        return () => {
            if (state === 'exit') {
                modalRoot.style.zIndex = '';
            }
        };
    }, [state, modalRoot, zIndex]);
    useEffect(() => {
        if (!scrollableRef.current) {
            return;
        }
        if (needRecalculation) {
            setTop(position(scrollableRef.current));
        }
    }, [needRecalculation]);
    useEffect(() => {
        if (!scrollableRef.current || !contentRef.current) {
            return;
        }
        const container = scrollableRef.current;
        if (needRecalculation) {
            container.addEventListener('scroll', calculateCloseTop);
        }
        return () => {
            container.removeEventListener('scroll', calculateCloseTop);
        };
    }, [calculateCloseTop, needRecalculation]);
    useKeyPress(keys, closable ? handleClose : void 0);
    useScrollLock(scrollableRef, state === 'show' || state === 'exit', Number(fit));
    const { area } = useSwipe({ onDownSwipe: handleSwipeDown, yThreshold: swipeThreshold });
    const attachContentRefs = useCallback(node => {
        if (swipeToClose) {
            area.current = node;
        }
        scrollableRef.current = node;
    }, [area, swipeToClose]);
    if (!modalRoot || state === 'init' || state === 'idle') {
        return null;
    }
    const containerStyles = Object.assign({}, (!!scrollShift && { paddingRight: scrollShift }));
    return createPortal(React.createElement("div", Object.assign({ className: classNames(s.container, s[size], {
            [s.in]: state === 'show',
            [s.out]: state === 'exit',
            [s.mobile]: mobileView,
        }), style: Object.assign({}, containerStyles), ref: containerRef }, prevention),
        React.createElement("div", { className: s.overlay, onClick: closable ? handleClose : undefined }),
        React.createElement("div", { ref: attachContentRefs, className: classNames(s.scrollable, !fit && s.scroll) },
            closable && mobile && !mobileView && (React.createElement("div", { className: s.closable, onClick: handleClose, onKeyPress: handleKeyPressEnter(handleClose), tabIndex: 0, "data-test": dataTest && `${dataTest}-close`, role: "button", "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C" },
                React.createElement("div", { className: s.close },
                    React.createElement(Icon, { name: "times", size: "s" })))),
            React.createElement(ModalPreview, Object.assign({ ref: contentRef, className: classNames(s.content, overflow && s.overflow), "data-test": dataTest }, ignoreScroll),
                React.createElement(context.Provider, { key: key, value: contextValue }, children),
                closable && !mobile && (React.createElement("button", { className: s.close, onClick: handleClose, style: { top }, "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C", "data-test": dataTest && `${dataTest}-close` },
                    React.createElement(Icon, { name: "times", size: "s" })))))), modalRoot);
};
Modal.WideContent = WideContent;
Modal.Content = Content;
Modal.Picture = Picture;
Modal.Title = Title;
Modal.Actions = Actions;
Modal.StickyActions = StickyActions;
Modal.Header = Header;
//# sourceMappingURL=modal.js.map